<template>
    <Dialog :header="dialogTitle" v-model:visible="visible"
        :style="{ width: '900px' }" :modal="true" @keyup.enter="onConfirm">
        <div class="confirmation-content">
            <CustomForm :fields="formFields" v-model="data" />
            <div class="mt-4">
                <DataSourceEditor ref="datasourceEditor" v-model="data.datasource" idPrefix="workspace-datasource-"
                                  @connector="value => connectorType = value"/>
                <Message v-if="datasourceError" severity="error" class="mt-2">{{ datasourceError }}</Message>
            </div>
            <div v-if="connectorType === 'celonis'" class="mt-4">
                <CustomCaseTableFilters
                    v-model="data.filters"
                    :leadingObjectTable="celonisOptions.leading_object_table || ''"
                    noLeadingObjectMessage="Choose a data model and leading object table to configure workspace filters."
                    :errorMessage="errorMessage"
                />
            </div>
        </div>

        <template #footer>
            <Button rounded label="Cancel" icon="pi pi-times" @click="onCancel" class="p-button-text" data-cy="workspaceNameCancel" />
            <Button rounded :disabled="creatingElement" :label="confirmButtonLabel" icon="pi pi-check" @click.once="onConfirm" data-cy="workspaceNameConfirm" />
        </template>
    </Dialog>
</template>

<script setup>
import {ref, computed, watch} from 'vue';
import Button from 'primevue/button';
import Message from 'primevue/message';
import {useStore} from 'vuex';
import WorkspaceModel from "@/src/model/WorkspaceModel";
import CustomCaseTableFilters from "@/src/views/graph/run/CustomCaseTableFilters.vue";
import DataSourceEditor from "@/src/shared-components/DataSourceEditor.vue";
import {cloneJson} from "@/src/utils/connectorSchema";
import {isApiError} from "@/src/api/http";

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    editElement: WorkspaceModel,
    parent: WorkspaceModel
});

const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

const emit = defineEmits(['update:modelValue', 'confirm']);

const store = useStore();

const data = ref({
    name: '',
    datasource: null,
    filters: []
});
const datasourceEditor = ref(null);
const connectorType = ref(null);

// A new workspace starts from its parent's data source and filters.
const reset = (element) => {
    const source = element || props.parent;
    data.value = {
        name: element ? element.name || '' : '',
        datasource: source?.datasource ? cloneJson(source.datasource) : null,
        filters: source?.filters ? cloneJson(source.filters) : [],
    };
};

watch(() => props.editElement, reset, {immediate: true});

const error = ref({});
const creatingElement = ref(false);

const type = computed(() => props.editElement?.parent ? 'Folder' : 'Process');

const dialogTitle = computed(() => props.editElement ? `Edit ${type.value}` : `Create ${type.value}`);
const confirmButtonLabel = computed(() => props.editElement ? `Update` : `Create`);

const formFields = computed(() => [
    {
        elementName: 'InputText',
        dataField: 'name',
        type: 'text',
        label: `${type.value} Name`,
        required: true,
        invalid: error.value && error.value.name,
        invalidMessage: error.value.name,
    },
]);

// Celonis workspaces: the filter editor reads tables and columns of the chosen data model
const celonisOptions = computed(() => (connectorType.value === 'celonis' ? data.value.datasource?.input?.options || {} : {}));

watch(() => [celonisOptions.value.datapool_id, celonisOptions.value.datamodel_id], async ([datapoolId, datamodelId]) => {
    const context = store.state.repoStore.tableContext;
    if (context?.datamodelId === (datamodelId || null) && context?.datapoolId === (datapoolId || null)) {
        return;
    }
    await store.dispatch('repoStore/loadDatamodelContext', {datapoolId: datapoolId || null, datamodelId: datamodelId || null});
}, {immediate: true});

watch(() => celonisOptions.value.leading_object_table, (leadingObjectTable, previous) => {
    if (previous !== undefined && !leadingObjectTable) {
        data.value.filters = [];
    }
});

const onCancel = () => {
    emit('update:modelValue', false);
};

const errorMessage = computed(() => {
    if (error.value?.filters && error.value.filters.length) {
        return error.value.filters[0]
    }
    return ''
})

const datasourceError = computed(() => {
    const value = error.value?.datasource;
    return Array.isArray(value) ? value[0] : value || '';
});

const onConfirm = async () => {
    if (creatingElement.value) return;
    creatingElement.value = true;

    const datasource = datasourceEditor.value ? datasourceEditor.value.submitValue() : data.value.datasource;
    const payload = {
        name: data.value.name,
        datasource: datasource && datasource.input?.connection_id ? datasource : null,
        // filters are edited for Celonis workspaces only, for now
        filters: connectorType.value === 'celonis' && celonisOptions.value.leading_object_table ? (data.value.filters || []) : [],
    };

    try {
        if (props.editElement) {
            await store.dispatch('repoStore/updateWorkspace', {
                workspaceId: props.editElement.id,
                data: payload,
            });
        } else {
            payload.parent = props.parent ? props.parent.id : null;
            await store.dispatch('repoStore/addSpace', payload);
        }
        emit('confirm');
        emit('update:modelValue', false);
    } catch (e) {
        error.value = isApiError(e) && e.isValidationError ? e.fieldErrors : {};
    } finally {
        creatingElement.value = false;
    }
};
</script>
